import cn from 'classnames';
import { ReactNode, TouchEvent, UIEvent, useRef, useState } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';

import { Icon } from '../Icon';

import styles from './Dialog.module.scss';

export type DialogProps = Omit<
  RadixDialog.DialogProps,
  'open' | 'onOpenChange'
> & {
  open?: boolean;
  onOpenChange(open: boolean): void;
  trigger?: JSX.Element;
  title?: ReactNode | string;
  withoutCloseButton?: boolean;
  className?: string;
  defaultOpen?: boolean;
};

export function Dialog({
  open,
  onOpenChange,
  trigger,
  title,
  withoutCloseButton = false,
  className,
  children,
  defaultOpen = false,
}: DialogProps): JSX.Element {
  const triggerContainer = useRef<HTMLDivElement>(null);
  const dialogRoot = document.getElementById('dialog');

  const [startingPosition, setStartingPosition] = useState<number>();
  const [deltaY, setDeltaY] = useState<number>();
  const [scrollTop, setScrollTop] = useState(0);
  const [swipeIsCancelling, setSwipeIsCancelling] = useState(false);

  const style =
    deltaY !== undefined
      ? {
          transform: `translate(-50%, ${deltaY}px)`,
        }
      : undefined;

  const handleClose = (): void => {
    setDeltaY(undefined);
    onOpenChange(false);
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>): void => {
    setScrollTop(event.currentTarget.scrollTop);
  };

  const onTouchStart = (event: TouchEvent<HTMLDivElement>): void => {
    setStartingPosition(event.changedTouches[0].clientY);
  };

  const onTouchMove = (event: TouchEvent<HTMLDivElement>): void => {
    if (scrollTop > 1) return;

    if (startingPosition !== undefined) {
      const delta = event.changedTouches[0].clientY - startingPosition;

      if (delta > 0) {
        setDeltaY(delta);
      }
    }
  };

  const onTouchEnd = (): void => {
    if (deltaY !== undefined && startingPosition !== undefined) {
      const swipePercent = deltaY / (window.innerHeight - startingPosition);

      if (swipePercent > 0.25) {
        handleClose();
      }
    }
    setSwipeIsCancelling(true);

    setDeltaY(undefined);
    setStartingPosition(undefined);

    setTimeout(() => setSwipeIsCancelling(false), 150);
  };

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Trigger asChild>
        <div ref={triggerContainer}>{trigger}</div>
      </RadixDialog.Trigger>
      <RadixDialog.Portal
        container={
          trigger !== undefined ? triggerContainer.current : dialogRoot
        }
      >
        <RadixDialog.Overlay className={styles.overlay} onClick={handleClose} />
        <RadixDialog.Content
          className={cn(className, styles.content, {
            [styles.swipeIsCancelling]: swipeIsCancelling,
          })}
          style={style}
          onScroll={handleScroll}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {title !== undefined && (
            <RadixDialog.Title className={styles.title}>
              {title}
            </RadixDialog.Title>
          )}
          {!withoutCloseButton && (
            <RadixDialog.Close
              className={styles.closeButton}
              onClick={handleClose}
            >
              <Icon icon="close" className={styles.closeIcon} />
            </RadixDialog.Close>
          )}
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
